import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { instruments } from "@/data/instruments";
import TrustBadge from "@/components/TrustBadge";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const handpanRotate = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const handpanScale = useTransform(scrollYProgress, [0, 1], [1, 0.6]);

  const words = ["Trouvez", "votre", "harmonie"];

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] overflow-hidden bg-cream flex items-center"
      data-testid="hero-section"
    >
      {/* Soft gradient blobs */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute top-[15%] -left-32 w-[500px] h-[500px] rounded-full bg-terracotta/15 blur-[100px]" />
        <div className="absolute bottom-[10%] -right-40 w-[600px] h-[600px] rounded-full bg-sage/20 blur-[120px]" />
        <div className="absolute top-[50%] left-[40%] w-[400px] h-[400px] rounded-full bg-[#A8843F]/10 blur-[90px]" />
      </motion.div>

      {/* Huge rotating handpan background element */}
      <motion.img
        src={instruments[0].image}
        alt=""
        aria-hidden="true"
        style={{ rotate: handpanRotate, scale: handpanScale, mixBlendMode: "multiply" }}
        className="absolute -right-[20%] top-[5%] w-[110vh] max-w-none pointer-events-none select-none"
        data-testid="hero-handpan-bg"
      />

      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 w-full pt-28 pb-16"
      >
        {/* Trust badge */}
        <TrustBadge />

        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex items-center gap-3 mb-10"
          data-testid="hero-eyebrow"
        >
          <div className="w-10 h-[1px] bg-terracotta" />
          <span className="text-xs uppercase tracking-[0.3em] text-ink/70">
            1<sup>re</sup> boutique de sonothérapie — Oise
          </span>
        </motion.div>

        {/* Headline */}
        <h1 className="font-serif text-[14vw] md:text-[10vw] lg:text-[8.5vw] leading-[0.85] font-light text-ink max-w-[12ch]">
          {words.map((w, i) => (
            <motion.span
              key={w}
              initial={{ opacity: 0, y: 80, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{
                duration: 1.1,
                delay: 0.4 + i * 0.12,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="block"
              data-testid={`hero-word-${w}`}
            >
              {i === 2 ? (
                <span className="italic-serif font-light text-terracotta">
                  {w}
                </span>
              ) : (
                w
              )}
              {i !== words.length - 1 && " "}
            </motion.span>
          ))}
        </h1>

        {/* Subhead */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
            className="mt-10 max-w-xl text-lg font-light leading-relaxed text-white md:text-xl md:text-ink/75"
          data-testid="hero-subhead"
        >
          Handpans, gongs, carillons et essentiels bien-être.
          Une sélection sensible — choisie pour la beauté du son et la
          singularité de chaque instrument.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <a
            href="https://www.yogimarket.fr/category/all-products"
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ink text-cream text-sm uppercase tracking-[0.15em] hover:bg-terracotta transition-all duration-500 hover:scale-[1.03]"
            data-testid="hero-cta-primary"
          >
            Explorer la boutique
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform"
            />
          </a>
          <a
            href="#showcase"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full border border-ink/20 text-ink text-sm uppercase tracking-[0.15em] hover:border-terracotta hover:text-terracotta transition-all duration-500"
            data-testid="hero-cta-secondary"
          >
            Découvrir nos instruments
          </a>
        </motion.div>

        {/* Location chip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-20 flex items-center gap-2 text-sm text-ink/60"
          data-testid="hero-location"
        >
          <MapPin size={14} className="text-terracotta" />
          17 rue de Pierrefonds, Compiègne — Retrait en boutique possible
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-ink/50">
          scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-[1px] h-12 bg-ink/30"
        />
      </motion.div>
    </section>
  );
}
